import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

// Current auth version — bump this to force all users to re-login
const AUTH_VERSION = '2';

/**
 * Decode and validate a JWT client-side (no server call needed).
 * Returns true only if:
 *  - It is a valid 3-part JWT
 *  - exp is a Unix timestamp number (not an ISO string from the old broken format)
 *  - The token is not expired
 */
function isTokenValid(token) {
  try {
    if (!token || token === 'null' || token === 'undefined') return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    // Decode payload (handle base64url → base64)
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

    // Reject old broken format where exp was an ISO string
    if (typeof payload.exp !== 'number') return false;

    // Reject expired tokens (60-second grace window)
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp < now - 60) return false;

    return true;
  } catch {
    return false;
  }
}

function clearAuthStorage() {
  localStorage.removeItem('jwt_token');
  localStorage.removeItem('user_info');
  localStorage.removeItem('auth_version');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from localStorage on mount — validate token before trusting it
  useEffect(() => {
    const storedVersion = localStorage.getItem('auth_version');
    const storedToken = localStorage.getItem('jwt_token');
    const storedUser = localStorage.getItem('user_info');

    // Force re-login if auth version changed (catches format-breaking releases)
    if (storedVersion !== AUTH_VERSION) {
      clearAuthStorage();
      setIsLoading(false);
      return;
    }

    if (storedToken && storedUser && isTokenValid(storedToken)) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch {
        clearAuthStorage();
      }
    } else if (storedToken) {
      // Token exists but is invalid/expired — clear it so user sees login page
      clearAuthStorage();
    }

    setIsLoading(false);
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('jwt_token', authToken);
    localStorage.setItem('user_info', JSON.stringify(userData));
    localStorage.setItem('auth_version', AUTH_VERSION);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    clearAuthStorage();
  };

  const getAuthHeader = () => {
    const authToken = token || localStorage.getItem('jwt_token');
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, getAuthHeader, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
